import React from 'react'
import { Text, View, remToPixels, useDripsyTheme } from 'dripsy'
import { Sizes, Variants, Filling } from '../types'
import type { SxStyleProp } from '@theme-ui/core'
import Ionicons from '@expo/vector-icons/build/Ionicons'
import useGetColors from '../../hooks/use-variant-colors'

type Icon =
  | {
      name: React.ComponentProps<typeof Ionicons>['name']
      size?: number
      // color?: string
    }
  | React.ReactElement
  | React.ComponentProps<typeof Ionicons>['name']

type Props = {
  children?: React.ReactNode
  variant?: Variants
  size?: Sizes
  dot?: boolean
  sx?: SxStyleProp
  textSx?: SxStyleProp
  uppercase?: boolean
  /**
   * @deprecated use `filling` instead
   * Default: `filled`. Can also be `faded`, or `outline`
   */
  type?: 'filled' | 'faded' | 'outline'
  /**
   * Default: `filled`
   */
  filling?: 'filled' | 'faded' | 'outlined'
  iconLeft?: Icon | boolean
  iconRight?: Icon | boolean
} & React.ComponentProps<typeof Text>

export default function Badge(props: Props) {
  const {
    sx = {},
    variant = 'primary',
    children,
    uppercase,
    size = 'small',
    dot,
    type = 'filled',
    filling = type === 'outline' ? 'outlined' : type,
    iconLeft,
    iconRight,
    textSx = {},
  } = props

  const { theme } = useDripsyTheme()
  const colors = {
    ...(theme.colors as any),
    // secondary: theme.colors?.mutedText,
  }

  const getFontSize = () => {
    const sizes: { [key in Sizes]: number } = {
      mini: remToPixels('.7rem'),
      small: remToPixels('.75rem'),
      medium: remToPixels('.875rem'),
      large: remToPixels('1rem'),
      xl: remToPixels('1.15rem'),
    }
    return sizes[size]
  }
  const getBgColor = () => {
    if (type === 'outline') return 'transparent'
    if (type === 'faded') {
      const lighter = colors?.[`${variant}Lighter`]
      if (lighter) {
        return lighter
      }
      return `${colors?.[variant]}20`
    }
    if (variant === 'default') {
      return colors?.text
    }
    return colors?.[variant]
  }

  const getTextColor = () => {
    let color = 'text'
    if (
      // TODO change this to a color library that checks if it's light or dark
      variant === 'primary' ||
      variant === 'success' ||
      variant === 'callout'
    ) {
      color = 'background'
    }
    if (type === 'outline' && colors?.[variant]) {
      color = colors?.[variant]
    } else if (type === 'faded' && colors?.[variant]) {
      color = colors?.[variant]
    }
    return color
  }

  const getBorderColor = () => {
    return colors?.[variant] ?? getTextColor()
  }

  // const bg = getBgColor()
  // const color = getTextColor()
  const fontSize = getFontSize()
  // const borderColor = getBorderColor()

  // let filling: Filling = Filling.FILLED
  // if (type === 'outline') {
  //   filling = Filling.OUTLINED
  // } else if (type === 'faded') {
  //   filling = Filling.FADED
  // } else if (type === 'filled') {
  //   filling = Filling.FILLED
  // }

  const { bg, color, borderColor } = useGetColors({
    variant,
    filling,
  })

  const maybeRenderIcon = (
    icon: Props['iconLeft'],
    side: 'left' | 'right' = 'left'
  ) => {
    if (React.isValidElement(icon)) {
      return icon
    }
    // this exists for conditional props
    if (typeof icon === 'boolean') return null

    let name: React.ComponentProps<typeof Ionicons>['name'] | null = null
    let size = 22
    if (typeof icon === 'string') {
      name = icon as React.ComponentProps<typeof Ionicons>['name']
    } else if (icon?.name) {
      name = icon.name
      if (icon.size) size = icon.size
    }

    if (name) {
      return (
        <Ionicons
          style={{
            marginRight:
              side === 'left' && children ? theme.space?.[1] * 1.5 : 0,
            marginLeft:
              side === 'right' && children ? theme.space?.[1] * 1.5 : 0,
            alignItems: 'center',
            height: size * 0.7,
            display: 'flex',
            justifyContent: 'center',
          }}
          color={color}
          size={size}
          name={name}
        />
      )
    }
    return null
  }

  return (
    <View
      sx={{
        borderRadius: dot ? 'rounded' : 4,
        bg,
        borderColor,
        borderWidth: 1,
        px: dot ? undefined : 2,
        py: dot ? undefined : 1,
        flexDirection: 'row',
        alignItems: 'center',
        height: dot ? 12 : undefined,
        width: dot ? 12 : undefined,
        ...sx,
      }}
    >
      {maybeRenderIcon(iconLeft, 'left')}
      <Text
        sx={{
          textTransform: uppercase ? 'uppercase' : undefined,
          color,
          fontSize: 1,
          fontWeight: '600',
          ...textSx,
        }}
      >
        {!dot && children}
      </Text>
      {maybeRenderIcon(iconRight, 'right')}
    </View>
  )
}
